import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LS_TABLE_COLUMNS, LS_TABLE_PARAMS } from 'app/modules/global/localstorage';
import { EnvService } from 'environments/env.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TableParamsHttpInterceptor implements HttpInterceptor {
  constructor(private readonly _envService: EnvService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.method !== 'GET') {
      return next.handle(req);
    }

    // Try to use cached request if available
    const cachedRequest = this.getCachedRequest(req);
    if (cachedRequest) {
      req = cachedRequest;
    }

    const filtersStr = req.params.get('filters');
    if (!filtersStr || typeof filtersStr !== 'string') {
      return next.handle(req);
    }

    let filters;
    try {
      filters = JSON.parse(filtersStr);
    } catch (err) {
      console.warn('Error parsing filters', err);
      return next.handle(req);
    }

    if (!filters) {
      return next.handle(req);
    }

    // Helper to clone request with new filters
    const cloneWithFilters = (newFilters: any) => {
      const httpParams = req.params.keys().reduce((acc, val) => {
        acc[val] = req.params.get(val);
        return acc;
      }, {});
      httpParams['filters'] = JSON.stringify(newFilters);
      return req.clone({ setParams: httpParams });
    };

    // FEED CONTENTS: filters is an object (not array)
    if (!Array.isArray(filters) && typeof filters === 'object') {
      Object.keys(filters).forEach((k) => {
        const v = filters[k];
        if (v.type === 'text' && typeof v.value === 'string') {
          v.value = v.value.replace(/[[\]()\\]/g, '\\$&');
        }
      });
      req = cloneWithFilters(filters);
    }

    // ALL TABLES: filters is an array
    if (Array.isArray(filters)) {
      const newFilters = filters.map((v) => {
        if (v.type === 'search' && typeof v.value === 'string') {
          v.value = v.value.replace(/[[?*\]()\\]/g, '\\$&');
        }
        return v;
      });
      req = cloneWithFilters(newFilters);
    }

    return next.handle(req);
  }

  getCachedRequest(req) {
    // Extract the URL path after the baseurl, if present
    const reqUrls = req.url.split(this._envService.baseurl());
    let reqUrl = reqUrls.length === 2 ? reqUrls[1] : reqUrls[0];

    // console.log('interceptor reqUrl', reqUrl);

    // Only process if URL exists and has no query params
    if (!reqUrl || req.params.keys().length) {
      return null;
    }

    const tableParams = this.getCachedTableParamsObject();
    const cachedParams = tableParams[reqUrl];

    // Only process if there are cached params for this URL
    if (!cachedParams || !Object.keys(cachedParams).length) {
      return null;
    }

    let cachedFilters = cachedParams.filters && JSON.parse(cachedParams.filters);

    // If filters are an array, filter out hidden column filters
    if (Array.isArray(cachedFilters)) {
      const tableColumns = this.getCachedTableColumnsObject();
      const cachedColumns = tableColumns[reqUrl];

      if (cachedColumns && Object.keys(cachedColumns).length) {
        cachedFilters = cachedFilters.filter((filter) => cachedColumns[filter.field] !== 0);
        cachedParams.filters = JSON.stringify(cachedFilters);
        this.setCachedTableParamsObject(tableParams);
      }
    }

    // Overwrite request query params with cached params
    return req.clone({
      setParams: cachedParams,
    });
  }

  getCachedTableParamsObject() {
    return JSON.parse(localStorage.getItem(LS_TABLE_PARAMS)) || {};
  }

  setCachedTableParamsObject(obj) {
    window.localStorage.setItem(LS_TABLE_PARAMS, JSON.stringify(obj));
  }

  getCachedTableColumnsObject() {
    return JSON.parse(localStorage.getItem(LS_TABLE_COLUMNS)) || {};
  }
}
